import {
    OrderItemViewModel,
    UsedItems,
    UsedModifier,
    UsedModifiers,
} from 'store/models/sellModels'
import { mockDiscountForComp } from '../discount/mockDiscountForComp'
import {
    calculateTotalDiscountsAmount,
    IDiscountResult,
} from 'views/Sale/utils/orderCalculation/discountCalculation'
import { calculateLineSubtotal } from 'views/Sale/utils/orderCalculation/common'
import { isCompItem } from 'views/Sale/utils/orderItemUtil'
import { cloneDeep, keys } from 'lodash-es'
import Decimal from 'decimal.js'
const LEAST_DIVIDED_PRICE = 0.01
function getEvenlyDividing(total: Decimal, shareCount: number) {
    // find number of fragmentable units; dCount = total / LEAST_DIVIDED_PRICE = total * 100
    const dCount = total.mul(100).floor()
    // number of units (0.01) per share
    const unitPerShare = dCount.div(shareCount).floor()

    // number of remaining units
    const remaining = dCount.mod(shareCount)

    const results: Decimal[] = Array<Decimal>(shareCount)
    for (let i = 0; i < shareCount; i++) {
        results[i] = unitPerShare.mul(LEAST_DIVIDED_PRICE)
    }

    for (let i = 0; remaining.greaterThan(i); i++)
        results[i] = results[i].plus(LEAST_DIVIDED_PRICE)

    return results
}

function calculateSubSharedLines(
    item: OrderItemViewModel,
    lineDiscounts: IDiscountResult[],
    totalCompDiscount: Decimal,
) {
    const subSharedLines: OrderItemViewModel[] = []
    const shareCount = item.assignTo?.length ?? 1
    if (shareCount > 1) {
        const distributedLineTotal = getEvenlyDividing(
            new Decimal(item.lineTotal),
            shareCount,
        )
        const distributedSubtotals = getEvenlyDividing(
            item.subtotal,
            shareCount,
        )

        const distributedItemDiscountAmounts: Decimal[] = []
        for (let i = 0; i < shareCount; i++) {
            distributedItemDiscountAmounts.push(new Decimal(0))
        }

        lineDiscounts.forEach((lineDis) => {
            const disLineDiscounts = getEvenlyDividing(
                lineDis.amount,
                shareCount,
            )
            disLineDiscounts.forEach((d, i) => {
                distributedItemDiscountAmounts[
                    i
                ] = distributedItemDiscountAmounts[i].plus(d)
            })
        })

        const distributedTotalCompDiscounts = getEvenlyDividing(
            totalCompDiscount,
            shareCount,
        )

        const splitedUsedItems: { [i: number]: UsedItems } = {}

        keys(item.usedItems).forEach((path) => {
            const comboPrices = getEvenlyDividing(
                new Decimal(item.usedItems[path].addPrice),
                shareCount,
            )
            comboPrices.forEach((price, lineIndex) => {
                if (!splitedUsedItems[lineIndex])
                    splitedUsedItems[lineIndex] = {}
                splitedUsedItems[lineIndex][path] = {
                    ...cloneDeep(item.usedItems[path]),
                    addPrice: price.toNumber(),
                }
            })
            const splitedUsedModifiers = splitUsedModifiers(
                item.usedItems[path].usedModifiers,
                shareCount,
            )
            for (let lineIndex = 0; lineIndex < shareCount; lineIndex++) {
                splitedUsedItems[lineIndex][path].usedModifiers =
                    splitedUsedModifiers[lineIndex]
            }
        })

        const splitedUsedModifiers = splitUsedModifiers(
            item.usedModifiers,
            shareCount,
        )

        for (let lineIndex = 0; lineIndex < shareCount; lineIndex++)
            subSharedLines.push({
                ...item,
                isSubSharedOrderLine: true,
                isSharedOrderLine: false,
                assignTo: [item.assignTo![lineIndex]],
                subSharedLines: [],
                usedItems: splitedUsedItems[lineIndex],
                usedModifiers: splitedUsedModifiers[lineIndex],
                lineTotal: distributedLineTotal[lineIndex].toNumber(),
                subtotal: distributedSubtotals[lineIndex],
                lineTotalWithItemDiscount: distributedLineTotal[lineIndex]
                    .sub(distributedItemDiscountAmounts[lineIndex])
                    .toNumber(),
                itemDiscountAmount: distributedItemDiscountAmounts[
                    lineIndex
                ].toNumber(),
                totalCompDiscount: distributedTotalCompDiscounts[
                    lineIndex
                ].toNumber(),
            })
    }
    return subSharedLines
}

const calculateLine = (
    item: OrderItemViewModel,
    isCalculateCompAfterDiscount: boolean,
): OrderItemViewModel => {
    const { voidComp, isSubSharedOrderLine } = item
    //already calculation in sharedLine process
    if (isSubSharedOrderLine) return item
    const lineSubtotal = calculateLineSubtotal(item)

    const {
        totalDiscountAmount,
        discountResult,
    } = calculateTotalDiscountsAmount(
        lineSubtotal,
        item.discounts ?? [],
        item.amount,
    )

    const lineTotalAfterDiscounts = lineSubtotal.minus(totalDiscountAmount)
    const compDiscount = isCompItem(item)
        ? mockDiscountForComp(
              voidComp!,
              false,
              isCalculateCompAfterDiscount
                  ? lineTotalAfterDiscounts
                  : lineSubtotal,
              item.amount,
          )?.value ?? 0
        : 0

    const lineResult: OrderItemViewModel = {
        ...item,
        subtotal: lineSubtotal,
        itemDiscountAmount: totalDiscountAmount.toNumber(),
        lineTotalWithItemDiscount: lineTotalAfterDiscounts.toNumber(),
        totalCompDiscount: compDiscount,
    }
    lineResult.subSharedLines = calculateSubSharedLines(
        lineResult,
        discountResult,
        new Decimal(compDiscount),
    )

    return lineResult
}

export default calculateLine
function splitUsedModifiers(usedModifiers: UsedModifiers, shareCount: number) {
    const splitedUsedModifiers: { [i: number]: UsedModifiers } = {}
    keys(usedModifiers).forEach((path) => {
        usedModifiers[path].forEach((mod: UsedModifier) => {
            const splitedPrices = getEvenlyDividing(
                new Decimal(mod.addPrice),
                shareCount,
            )
            const splitedOptions: UsedModifier[] = []
            splitedPrices.forEach((price) => {
                splitedOptions.push({
                    modifierOptionId: mod.modifierOptionId,
                    addPrice: price.toNumber(),
                    subModifierOptions: {},
                })
            })
            keys(mod.subModifierOptions).forEach((subKey) => {
                mod.subModifierOptions[subKey].forEach((subOption) => {
                    const splitedSubPrices = getEvenlyDividing(
                        new Decimal(subOption.addPrice),
                        shareCount,
                    )
                    splitedSubPrices.forEach((price, lineIndex) => {
                        if (!splitedOptions[lineIndex].subModifierOptions[path])
                            splitedOptions[lineIndex].subModifierOptions[
                                path
                            ] = []
                        splitedOptions[lineIndex].subModifierOptions[path].push(
                            {
                                modifierOptionId: subOption.modifierOptionId,
                                addPrice: price.toNumber(),
                            },
                        )
                    })
                })
            })
            splitedOptions.forEach((o, lineIndex) => {
                if (!splitedUsedModifiers[lineIndex])
                    splitedUsedModifiers[lineIndex] = {}
                if (!splitedUsedModifiers[lineIndex][path])
                    splitedUsedModifiers[lineIndex][path] = []
                splitedUsedModifiers[lineIndex][path].push(o)
            })
        })
    })
    return splitedUsedModifiers
}
